/**
 * services/burden.ts — Burden Builder computation.
 *
 * Composes an employer's labor burden % from itemized inputs instead of asking
 * the user to guess one number. Pulls SUTA/WC defaults from reference data
 * based on (state, industry), then layers on health insurance and retirement.
 */

import type { BurdenComposition, BusinessSettings, Employee } from '../types';
import {
  FICA_PERCENT,
  FUTA_EFFECTIVE_PERCENT,
  MEDICARE_PERCENT,
  SOCIAL_SECURITY_PERCENT,
  SOCIAL_SECURITY_WAGE_BASE,
  getSutaRate,
  getSutaWageBase,
  getWcRate,
} from '../data/burdenReferenceData';

export interface BurdenBreakdown {
  fica: number;            // federal, fixed
  futa: number;            // federal, fixed
  suta: number;            // state, sourced from defaults unless overridden
  workersComp: number;     // state × trade, sourced from defaults unless overridden
  healthInsurance: number; // converted from $/mo into % of wage
  retirement: number;      // % match
  otherBenefits: number;   // bonuses / PTO accrual
  total: number;           // sum of above — what gets applied as burden %
  notes: string[];         // human-readable explanations for the breakdown panel
}

const DEFAULT_BURDEN: BurdenComposition = {
  useBuilder: false,
};

/**
 * Compute the total burden % for an employee based on the workspace's builder
 * config. Returns a breakdown so the UI can show how each component contributes.
 *
 * @param settings  Workspace settings (industry + state come from profile)
 * @param employee  Used to convert health insurance ($/mo) into % of this
 *                  employee's wage. Pass `wage` and `payType` accurately.
 */
export function computeBurdenBreakdown(
  settings: BusinessSettings,
  employee: Pick<Employee, 'wage' | 'payType'> & { annualHours?: number }
): BurdenBreakdown {
  const burden = settings.burden ?? DEFAULT_BURDEN;
  const industry = settings.profile?.industryType;
  const state = settings.profile?.state;

  /* El sueldo anual de UNA persona. Es la base de todo lo que lleva tope:
   * el SUTA, el Seguro Social y la conversión del seguro médico. */
  const annualWage =
    employee.payType === 'Hourly'
      ? (employee.wage ?? 0) * (employee.annualHours ?? 1400) // fallback 1400 hrs
      : (employee.wage ?? 0);

  /* FICA con el tope del Seguro Social.
   *
   * El 6,2 % se deja de pagar pasada la base salarial; el 1,45 % de Medicare no
   * tiene tope. Para el 95 % de las nóminas de un contratista da exactamente
   * 7,65 % —nadie llega al tope— pero para el retiro de un dueño de 200.000 $
   * cambia de verdad, y ahora que existe la maquinaria del tope sale gratis. */
  const ssTaxable = annualWage > 0 ? Math.min(annualWage, SOCIAL_SECURITY_WAGE_BASE) : 0;
  const ssEffective = annualWage > 0
    ? (SOCIAL_SECURITY_PERCENT * ssTaxable) / annualWage
    : SOCIAL_SECURITY_PERCENT;
  const fica = annualWage > 0 ? ssEffective + MEDICARE_PERCENT : FICA_PERCENT;

  const futa = FUTA_EFFECTIVE_PERCENT;

  /* SUTA sobre su base salarial, no sobre toda la nómina.
   *
   * La tabla guarda el tipo estatutario (California 3,4 %, Nueva York 4,1 %),
   * que solo se paga sobre los primeros N dólares de cada empleado. Aplicarlo
   * al sueldo completo multiplicaba esta línea por tres o por siete. Se
   * convierte a porcentaje EFECTIVO del sueldo, que es la unidad en la que
   * trabaja el resto de la pila.
   *
   * Un `sutaOverride` escrito a mano se respeta tal cual: si el dueño teclea el
   * tipo de su carta anual, manda él. */
  const sutaRate = getSutaRate(state);
  const suta = burden.sutaOverride ?? (
    annualWage > 0
      ? (sutaRate * Math.min(annualWage, getSutaWageBase(state))) / annualWage
      : sutaRate
  );

  const workersComp = burden.wcOverride ?? getWcRate(industry, state);

  // Convert health insurance from $/mo into % of annual wage.
  let healthInsurance = 0;
  if (burden.healthInsurancePerEmployeeMonthly && employee) {
    const annualHealthCost = burden.healthInsurancePerEmployeeMonthly * 12;
    healthInsurance = annualWage > 0 ? (annualHealthCost / annualWage) * 100 : 0;
  }

  const retirement = burden.retirementMatchPercent ?? 0;
  const otherBenefits = burden.otherBenefitsPercent ?? 0;

  const total = fica + futa + suta + workersComp + healthInsurance + retirement + otherBenefits;

  const notes: string[] = [];
  if (!state) notes.push('No state set — using national-average SUTA/WC. Add your state for accuracy.');
  if (burden.healthInsurancePerEmployeeMonthly && employee.payType === 'Hourly' && !employee.annualHours) {
    notes.push('Health insurance % computed assuming 1,400 hrs/yr. Adjust season for accuracy.');
  }

  return {
    fica,
    futa,
    suta,
    workersComp,
    healthInsurance,
    retirement,
    otherBenefits,
    total,
    notes,
  };
}

/**
 * Returns the effective burden % to apply to an employee.
 *
 * If `useBuilder` is true, computes from the builder stack.
 * Otherwise returns the employee's typed burden %.
 *
 * This is the SINGLE function metrics.ts should call — never read
 * `emp.laborBurdenPercent` directly when in builder mode.
 */
export function effectiveBurdenPercent(
  settings: BusinessSettings,
  employee: Employee & { annualHours?: number }
): number {
  if (settings.burden?.useBuilder) {
    return computeBurdenBreakdown(settings, employee).total;
  }
  return employee.laborBurdenPercent ?? 0;
}
